import React, { useEffect, useState } from "react"
import {
  Container,
  Button,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Spinner,
  Alert,
  Collapse,
} from "reactstrap"
import { useParams } from "react-router-dom"
import { Breadcrumbs } from "@material-ui/core"
import toastr from "toastr"
import {
  Timeline as MuiTimeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@material-ui/lab"
import {
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  GetApp as GetAppIcon,
  Description as DocumentIcon,
} from "@material-ui/icons"
import { getProgressByCNJSolucionare } from "helpers/blopsleads_helper"
import DocumentViewerModal from "./DocumentViewerModal"

const Timeline = () => {
  const { id } = useParams()
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(true)
  const [openYears, setOpenYears] = useState(new Set())
  const [selectedYear, setSelectedYear] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState(null)
  const [openAccordions, setOpenAccordions] = useState({})

  const fetchSolucionaProgress = async numeroCNJ => {
    try {
      const response = await getProgressByCNJSolucionare(numeroCNJ)
      if (response.length === 0) {
        setData({})
      } else {
        setData(response)
      }
    } catch (error) {
      toastr.error("Erro ao carregar os andamentos")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const loadData = () => {
      setLoading(true)
      fetchSolucionaProgress(id)
    }
    loadData()
  }, [])

  const yearsAvailable = data.progress ? Object.keys(data.progress) : []
  const firstYear =
    yearsAvailable.length > 0 ? Math.max(...yearsAvailable.map(Number)) : null

  useEffect(() => {
    if (firstYear) {
      setOpenYears(new Set([firstYear.toString()]))
      setSelectedYear(firstYear.toString())
    }
  }, [firstYear])

  const toggleYear = year => {
    const newOpenYears = new Set(openYears)
    if (newOpenYears.has(year)) {
      newOpenYears.delete(year)
    } else {
      newOpenYears.add(year)
    }
    setOpenYears(newOpenYears)
    setSelectedYear(year)
  }

  const getTimelineItemClasses = index => {
    const isEven = index % 2 === 0
    return {
      contentClasses: `d-flex ${
        isEven ? "justify-content-end" : "justify-content-start"
      } w-100`,
      cardClasses: `mb-3 border rounded p-3 ${
        isEven ? "ms-3" : "me-3"
      } shadow-sm transition-all hover:shadow-lg`,
      dateClasses: `text-secondary fs-6 mb-2 fw-medium text-${
        isEven ? "end" : "start"
      }`,
    }
  }

  const getDotColor = eventText => {
    const text = eventText.toLowerCase()
    if (text.includes("sentença") || text.includes("decisão")) {
      return "bg-danger"
    }
    if (text.includes("despacho") || text.includes("determinação")) {
      return "bg-success"
    }
    if (text.includes("petição") || text.includes("protocolo")) {
      return "bg-warning"
    }
    return "bg-primary"
  }

  let allProgress = []

  for (const year in data?.progress) {
    allProgress = allProgress.concat(data.progress[year])
  }

  const getUnassociatedDocumentsByYear = () => {
    if (!data.documents) return {}

    const unassociatedDocs = data.documents.filter(
      doc => !allProgress.some(event => event.codAndamento === doc.codAndamento)
    )

    return unassociatedDocs.reduce((acc, doc) => {
      const year = doc.date
        ? new Date(doc.date).getFullYear().toString()
        : "Sem Data"
      if (!acc[year]) {
        acc[year] = []
      }
      acc[year].push(doc)
      return acc
    }, {})
  }

  const toggleAccordion = year => {
    setOpenAccordions(prev => ({
      ...prev,
      [year]: !prev[year],
    }))
  }

  const getFileNameFromUrl = url => {
    return url.split("/").pop()
  }

  const renderUnassociatedDocuments = () => {
    const docsByYear = getUnassociatedDocumentsByYear()

    return (
      <div
        className="documents-accordion"
        style={{
          width: "300px",
          position: "absolute",
          right: "15px",
          zIndex: 1000,
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
          borderRadius: "4px",
        }}
      >
        {Object.entries(docsByYear).map(([year, docs]) => (
          <Card className="mb-2 border-0 shadow-sm" key={year}>
            <CardHeader
              className="bg-light py-2"
              onClick={() => toggleAccordion(year)}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <small className="fw-medium text-muted">
                  {/* {year} ({docs.length}) */}
                  Documentos sem Associação
                </small>
                <Button color="link" className="p-0" size="sm">
                  {openAccordions[year] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Button>
              </div>
            </CardHeader>
            <Collapse isOpen={openAccordions[year]}>
              <CardBody className="py-2">
                <div className="d-flex flex-column gap-2">
                  {docs.map((doc, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center p-2 border rounded bg-white"
                    >
                      <small className="text-truncate me-2">
                        {getFileNameFromUrl(doc.url) ||
                          "Documento sem descrição"}
                      </small>

                      <Button
                        color="link"
                        className="p-0 ms-2"
                        onClick={() => window.open(doc.url, "_blank")}
                      >
                        <DocumentIcon
                          className="text-primary"
                          style={{ fontSize: "1.2rem" }}
                        />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Collapse>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <React.Fragment>
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        </div>
      ) : (
        <Container fluid className="page-content">
          <Breadcrumbs
            title="Linha do Tempo"
            breadcrumbitem={`Processo ${id}`}
          />

          <Card>
            <CardHeader className="bg-white" style={{ position: "relative" }}>
              <Row className="align-items-start">
                <div className="d-flex justify-content-between w-100">
                  <div className="d-flex flex-column">
                    <h4 className="mb-1">
                      Linha do Tempo do Processo{" "}
                      <span style={{ fontWeight: "bold" }}>{id}</span>
                    </h4>
                    {firstYear &&
                      data.progress[firstYear] &&
                      data.progress[firstYear][0] && (
                        <small className="text-muted">
                          Última atualização: {data.progress[firstYear][0].date}
                        </small>
                      )}
                  </div>
                  {renderUnassociatedDocuments()}
                </div>
              </Row>
            </CardHeader>

            <CardBody>
              {yearsAvailable.length === 0 ? (
                <Alert color="warning">
                  Não existem andamentos para este processo.
                </Alert>
              ) : (
                <>
                  <div className="d-flex gap-2 mb-4 overflow-auto">
                    {yearsAvailable
                      .map(year => (
                        <Button
                          key={year}
                          color={
                            selectedYear === year ? "primary" : "secondary"
                          }
                          onClick={() => toggleYear(year)}
                          className="year-button"
                        >
                          {year}
                        </Button>
                      ))
                      .reverse()}
                  </div>
                  <MuiTimeline align="alternate">
                    {data?.progress[selectedYear]?.map((event, index) => {
                      const { contentClasses, cardClasses, dateClasses } =
                        getTimelineItemClasses(index)
                      const dotColor = getDotColor(event.text)
                      const associatedDocuments = (data.documents || []).filter(
                        doc => doc.codAndamento === event.codAndamento
                      )

                      return (
                        <TimelineItem key={index}>
                          <TimelineOppositeContent>
                            <div className={dateClasses}>{event.date}</div>
                          </TimelineOppositeContent>

                          <TimelineSeparator>
                            <TimelineDot className={dotColor} />
                            {index !==
                              data.progress[selectedYear].length - 1 && (
                              <TimelineConnector className="bg-secondary" />
                            )}
                          </TimelineSeparator>

                          <TimelineContent>
                            <Card className={cardClasses}>
                              <div className="text-dark d-flex justify-content-between align-items-center">
                                <p className="m-0 fw-normal">{event.text}</p>
                                {associatedDocuments.length > 0 && (
                                  <div className="d-flex gap-2">
                                    {associatedDocuments.map(
                                      (doc, docIndex) => (
                                        <Button
                                          key={docIndex}
                                          color="link"
                                          onClick={() =>
                                            window.open(doc.url, "_blank")
                                          }
                                        >
                                          <DocumentIcon className="text-primary" />
                                        </Button>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            </Card>
                          </TimelineContent>
                        </TimelineItem>
                      )
                    })}
                  </MuiTimeline>
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      )}
      <DocumentViewerModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        documentUrl={currentDocumentUrl}
      />
    </React.Fragment>
  )
}

export default Timeline
